import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router'

import { updateRentalPortalListingsTableConfig } from '../../../../../api'
import { EMPTY_LIST, EMPTY_OBJECT } from '../../../../../constants/common'
import { RENTAL_LISTING_ENTITY } from '../../../../../constants/entity'
import { SUBMENU_DEFAULT_VALUE } from '../../../../../constants/route'
import { useSelection, useTimeZone } from '../../../../../hooks'
import { isListViewTypeBoard } from '../../../../../utils/listViewType'
import { useAsyncTable, useTableRefresh } from '../../../../Table'
import { LISTINGS_EXTRA_FILTERS } from '../../RentalPortalListings.constants'
import {
  getListingsSavedViewType,
  getListingsTableViewType,
} from '../../RentalPortalListings.utils'
import { useRentalPortalListingsStatistic } from '../useRentalPortalListingsStatistic'

import {
  getHasListingsFilterParams,
  getListingsBoardListApiParams,
  getListingsBoardPagination,
  getListingsHasActiveFilters,
  getListingsInitialFilters,
  getListingsOrderedRangeFilters,
  getListingsSearchParamsWithoutFilters,
} from './useRentalPortalListingsTablePage.utils'

export function useRentalPortalListingsTablePage() {
  const [searchParams, setSearchParams] = useSearchParams()

  const timezone = useTimeZone()

  const [isConfigOpen, setConfigOpen] = useState(false)
  const [isBoardRegime, setBoardRegime] = useState(false)

  const pageScrollWrapperRef = useRef(null)

  function transformInitialFilters(filters) {
    if (getHasListingsFilterParams(searchParams)) {
      setSearchParams(getListingsSearchParamsWithoutFilters, { replace: true })
    }

    return getListingsInitialFilters({ filters, searchParams, timezone })
  }

  const asyncTable = useAsyncTable({
    containerRef: pageScrollWrapperRef,
    entity: RENTAL_LISTING_ENTITY,
    extraFilters: LISTINGS_EXTRA_FILTERS,
    getListApiParams: isBoardRegime ? getListingsBoardListApiParams : undefined,
    sub: SUBMENU_DEFAULT_VALUE,
    transformInitialFilters,
    transformSaveFilters: getListingsOrderedRangeFilters,
    withoutSubMenu: true,
  })

  const {
    columns,
    filterValues,
    handleLoadInit,
    handleReload: handleReloadTable,
    handleSetFilterValues,
    handleSetView,
    isRefetching,
    items,
    preview,
    view,
  } = asyncTable

  const listViewType = getListingsSavedViewType(view)

  const listings = items?.items ?? EMPTY_LIST

  const selection = useSelection({
    data: items,
    pagination: asyncTable.pagination,
  })

  const { handleCleanSelection } = selection

  const { handleReloadStatistic, statistic } =
    useRentalPortalListingsStatistic(filterValues)

  async function handleReload() {
    handleCleanSelection()

    handleReloadStatistic()

    return handleReloadTable()
  }

  const { refreshButtonProps } = useTableRefresh({
    isRefetching,
    onReload: handleReload,
  })

  if (isListViewTypeBoard(listViewType) !== isBoardRegime) {
    setBoardRegime(isListViewTypeBoard(listViewType))
  }

  useEffect(() => {
    handleLoadInit()
  }, [])

  useEffect(() => {
    handleCleanSelection()
  }, [filterValues])

  useEffect(() => {
    handleReloadTable()
  }, [isBoardRegime])

  async function handleApply(nextConfig) {
    return updateRentalPortalListingsTableConfig({
      columns,
      preview,
      view: listViewType,
      ...nextConfig,
    })
  }

  function handleConfigOpen() {
    setConfigOpen(true)
  }

  function handleClearFilters() {
    handleSetFilterValues(EMPTY_OBJECT)
  }

  function handleShowTableRegime() {
    const nextView = getListingsTableViewType(listViewType)

    handleSetView(nextView)

    handleApply({ view: nextView })
  }

  return {
    ...asyncTable,
    ...selection,
    entity: RENTAL_LISTING_ENTITY,
    handleApply,
    handleClearFilters,
    handleConfigOpen,
    handleReload,
    handleShowTableRegime,
    hasActiveFilters: getListingsHasActiveFilters(filterValues),
    isConfigOpen,
    listings,
    listViewType,
    massActionFilterValues: { ...filterValues, ...LISTINGS_EXTRA_FILTERS },
    pageScrollWrapperRef,
    pagination: isBoardRegime
      ? getListingsBoardPagination(asyncTable.pagination)
      : asyncTable.pagination,
    refreshButtonProps,
    setConfigOpen,
    statistic,
  }
}
